import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import GearIcon from "./GearIcon";
import { getHookSuggestions } from "./catch-form/catchFormGearHelpers";

const normalizeColorText = (value) =>
  typeof value === "string" ? value.trim() : "";

const buildLureColorSuggestions = (source) => {
  if (!source || typeof source !== "object") {
    return [];
  }

  const seen = new Set();

  return Object.entries(source).flatMap(([brand, colors]) => {
    if (!Array.isArray(colors)) {
      return [];
    }

    return colors.flatMap((colorName) => {
      const normalizedColor = normalizeColorText(colorName);
      if (!normalizedColor) {
        return [];
      }

      const dedupeKey = normalizedColor.toLowerCase();
      if (seen.has(dedupeKey)) {
        return [];
      }

      seen.add(dedupeKey);

      return [
        {
          key: dedupeKey,
          value: normalizedColor,
          searchText: [normalizedColor, normalizeColorText(brand)]
            .filter(Boolean)
            .join(" ")
            .toLowerCase(),
        },
      ];
    });
  });
};

function GearInput({
  rodDatabase,
  reelDatabase,
  braidDatabase,
  leaderDatabase,
  lureDatabase,
  hookBrandDatabase,
  baitDatabase,
  liveBaitDatabase,
  deadBaitDatabase,
  combinedHookOptions,
  lureSizeOptions,
  typeOfFishing,
  isLureFishing,
  rod,
  setRod,
  reel,
  setReel,
  braid,
  setBraid,
  leader,
  setLeader,
  lure,
  setLure,
  lureSize,
  setLureSize,
  lureColor,
  setLureColor,
  hookBrand,
  setHookBrand,
  hookSpec,
  setHookSpec,
  knotBraidToLeader,
  setKnotBraidToLeader,
  knotLeaderToLure,
  setKnotLeaderToLure,
}) {
  const { t } = useTranslation();

  const renderGearLabel = (iconType, label) => (
    <span className="catch-details-label-content gear-field-label-content">
      <GearIcon type={iconType} />
      <span>{label}</span>
    </span>
  );
  const containerRef = useRef(null);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const [braidToLeaderKnots, setBraidToLeaderKnots] = useState([]);
  const [leaderToLureKnots, setLeaderToLureKnots] = useState([]);
  const [lureColors, setLureColors] = useState([]);

  useEffect(() => {
    Promise.all([
      fetch(process.env.PUBLIC_URL + "/JSON/knotsBraidToLeader.json").then((res) => res.json()),
      fetch(process.env.PUBLIC_URL + "/JSON/knotsLeaderToLure.json").then((res) => res.json()),
      fetch(process.env.PUBLIC_URL + "/JSON/lureColors.json").then((res) => res.json()),
    ])
      .then(([braidKnots, leaderKnots, colors]) => {
        setBraidToLeaderKnots(braidKnots);
        setLeaderToLureKnots(leaderKnots);
        setLureColors(buildLureColorSuggestions(colors));
      })
      .catch((err) => console.error("❌ Failed to load JSON files:", err));
  }, []);

  useEffect(() => {
    const handlePointerDownOutside = (event) => {
      if (containerRef.current?.contains(event.target)) {
        return;
      }
      setActiveDropdown(null);
    };
    document.addEventListener("mousedown", handlePointerDownOutside);
    return () =>
      document.removeEventListener("mousedown", handlePointerDownOutside);
  }, []);

  const getSuggestions = (database, input) => {
    if (!input) return [];
    const lower = input.toLowerCase();
    return database
      .flatMap((b) =>
        b.models
          .filter((m) => `${b.brand} ${m}`.toLowerCase().includes(lower))
          .map((m) => `${b.brand} ${m}`)
      )
      .slice(0, 8);
  };

  const normalizeLineText = (value) =>
    typeof value === "string" ? value.trim() : "";

  const formatLineTypeLabel = (value) => {
    const normalized = normalizeLineText(value).toLowerCase();
    if (!normalized) return "";
    if (normalized === "braid") return "Braid";
    if (normalized === "monofilament") return "Mono";
    if (normalized === "fluorocarbon") return "Fluorocarbon";
    if (normalized === "fused") return "Fused";

    return normalized
      .split(/[\s_-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  };

  const formatLineXCount = (value) => {
    const normalized = normalizeLineText(
      value == null ? "" : String(value)
    ).toLowerCase();
    if (!normalized) return "";
    if (normalized.startsWith("x")) return normalized;
    return `x${normalized}`;
  };

  const formatLineMeasure = (value, suffix) => {
    if (value == null || value === "") return "";

    if (typeof value === "number" && Number.isFinite(value)) {
      return `${value} ${suffix}`;
    }

    const text = normalizeLineText(String(value));
    if (!text) return "";
    if (text.toLowerCase().includes(suffix.toLowerCase())) {
      return text;
    }

    return `${text} ${suffix}`;
  };

  const buildLineLabel = (brand, model) =>
    [normalizeLineText(brand), normalizeLineText(model)]
      .filter(Boolean)
      .join(" ")
      .trim();

  const buildLineSubtitle = (modelEntry, sizeEntry) => {
    const parts = [
      formatLineXCount(modelEntry?.x),
      formatLineMeasure(sizeEntry?.mm, "mm"),
      formatLineMeasure(sizeEntry?.lb, "lb"),
      formatLineTypeLabel(modelEntry?.type),
    ].filter(Boolean);

    return parts.join(" · ");
  };

  const buildLineSearchText = (brand, modelEntry, label, subtitle, sizeEntry) =>
    [
      normalizeLineText(brand),
      normalizeLineText(modelEntry?.model),
      normalizeLineText(modelEntry?.type),
      formatLineTypeLabel(modelEntry?.type),
      formatLineXCount(modelEntry?.x),
      formatLineMeasure(sizeEntry?.mm, "mm"),
      formatLineMeasure(sizeEntry?.lb, "lb"),
      label,
      subtitle,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

  const getLineSuggestions = (database, input) => {
    if (!input || !Array.isArray(database)) return [];
    const lower = input.toLowerCase();
    const seen = new Set();

    return database
      .flatMap((brandEntry) => {
        const brand = normalizeLineText(brandEntry?.brand);
        const models = Array.isArray(brandEntry?.models) ? brandEntry.models : [];

        return models.flatMap((modelEntry) => {
          const model = normalizeLineText(modelEntry?.model);
          const label = buildLineLabel(brand, model);
          if (!label) {
            return [];
          }

          const sizeRows =
            Array.isArray(modelEntry?.sizes) && modelEntry.sizes.length > 0
              ? modelEntry.sizes
              : [null];

          return sizeRows.map((sizeEntry, index) => {
            const subtitle = buildLineSubtitle(modelEntry, sizeEntry);
            const value = subtitle ? `${label} — ${subtitle}` : label;
            const searchText = buildLineSearchText(
              brand,
              modelEntry,
              label,
              subtitle,
              sizeEntry
            );

            return {
              key: `${brand}-${model}-${subtitle || "base"}-${index}`,
              value,
              displayName: label,
              displaySubtitle: subtitle,
              searchText,
            };
          });
        });
      })
      .filter((suggestion) => {
        if (!suggestion || !suggestion.searchText.includes(lower)) {
          return false;
        }

        const dedupeKey = suggestion.value.toLowerCase();
        if (seen.has(dedupeKey)) {
          return false;
        }

        seen.add(dedupeKey);
        return true;
      })
      .slice(0, 8);
  };

  const normalizeLureText = (value) =>
    typeof value === "string" ? value.trim() : "";

  const buildLureComposedLabel = (lureItem) =>
    [
      normalizeLureText(lureItem?.brand),
      normalizeLureText(lureItem?.model),
      normalizeLureText(lureItem?.submodel),
      normalizeLureText(lureItem?.buoyancy),
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

  const buildLureDisplayLabel = (lureItem) => {
    const displayName = normalizeLureText(lureItem?.display_name);
    const composedLabel = buildLureComposedLabel(lureItem);
    const idFallback = normalizeLureText(lureItem?.id);

    return displayName || composedLabel || idFallback;
  };

  const buildLureDisplaySubtitle = (lureItem, displayLabel) => {
    const displayName = normalizeLureText(lureItem?.display_name);
    const composedLabel = buildLureComposedLabel(lureItem);

    if (
      displayName &&
      composedLabel &&
      composedLabel.toLowerCase() !== displayLabel.toLowerCase()
    ) {
      return composedLabel;
    }

    return "";
  };

  const buildLureSearchText = (lureItem, displayLabel, displaySubtitle) =>
    [
      normalizeLureText(lureItem?.id),
      normalizeLureText(lureItem?.brand),
      normalizeLureText(lureItem?.model),
      normalizeLureText(lureItem?.submodel),
      normalizeLureText(lureItem?.buoyancy),
      normalizeLureText(lureItem?.display_name),
      displayLabel,
      displaySubtitle,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

  const getLureSuggestions = (database, input) => {
    if (!input || !Array.isArray(database)) return [];
    const lower = input.toLowerCase();
    const seen = new Set();

    return database
      .map((lureItem) => {
        const displayLabel = buildLureDisplayLabel(lureItem);
        if (!displayLabel) {
          return null;
        }

        const displaySubtitle = buildLureDisplaySubtitle(lureItem, displayLabel);
        const searchText = buildLureSearchText(
          lureItem,
          displayLabel,
          displaySubtitle
        );

        return {
          key: normalizeLureText(lureItem?.id) || displayLabel,
          value: displayLabel,
          displayName: displayLabel,
          displaySubtitle,
          searchText,
        };
      })
      .filter((suggestion) => {
        if (!suggestion || !suggestion.searchText.includes(lower)) {
          return false;
        }

        const dedupeKey = suggestion.value.toLowerCase();
        if (seen.has(dedupeKey)) {
          return false;
        }

        seen.add(dedupeKey);
        return true;
      })
      .slice(0, 8);
  };

  const getBaitSuggestions = (input) => {
    if (!input) return [];
    const lower = input.toLowerCase();

    let database = baitDatabase;
    if (typeOfFishing && typeOfFishing.toLowerCase().includes("live bait")) {
      database = liveBaitDatabase;
    } else if (typeOfFishing && typeOfFishing.toLowerCase().includes("dead bait")) {
      database = deadBaitDatabase;
    }

    if (!database || database.length === 0) return [];

    if (typeof database[0] === "string") {
      return database.filter((item) => item.toLowerCase().includes(lower)).slice(0, 8);
    } else {
      return database
        .filter((b) => {
          const nameMatch = b.name?.toLowerCase().includes(lower);
          const baseMatch = b.base?.toLowerCase().includes(lower);
          const categoryMatch = b.category?.toLowerCase().includes(lower);
          return nameMatch || baseMatch || categoryMatch;
        })
        .map((b) => b.name)
        .slice(0, 8);
    }
  };

  const getColorSuggestions = (input) => {
    if (!input) return [];
    const lower = input.toLowerCase();
    return lureColors
      .filter((suggestion) => suggestion.searchText.includes(lower))
      .slice(0, 8);
  };

  const getBraidKnotSuggestions = (input) => {
    if (!input) return [];
    const lower = input.toLowerCase();
    return braidToLeaderKnots
      .filter((k) => k.toLowerCase().includes(lower))
      .slice(0, 8);
  };

  const getLeaderKnotSuggestions = (input) => {
    if (!input) return [];
    const lower = input.toLowerCase();
    return leaderToLureKnots
      .filter((k) => k.toLowerCase().includes(lower))
      .slice(0, 8);
  };

  const braidSuggestions =
    activeDropdown === "Braid" && braid
      ? getLineSuggestions(braidDatabase, braid)
      : [];
  const lureSuggestions =
    activeDropdown === "Lure" && lure ? getLureSuggestions(lureDatabase, lure) : [];

  return (
    <div ref={containerRef} className="gear-input-fields">
      {/* BRAID */}
      <div className="catch-form-field">
        <label>{renderGearLabel("line", `${t("gear_braid_mono")}:`)}</label>
        <div style={{ position: "relative" }}>
          <input
            type="text"
            placeholder={t("enter_braid")}
            value={braid}
            onFocus={() => setActiveDropdown("Braid")}
            onChange={(e) => setBraid(e.target.value)}
          />
          {braidSuggestions.length > 0 && (
              <ul className="gear-suggestions">
                {braidSuggestions.map((suggestion, index) => (
                  <li
                    key={`${suggestion.key}-${index}`}
                    onClick={() => {
                      setBraid(suggestion.value);
                      setActiveDropdown(null);
                    }}
                  >
                    <div>{suggestion.displayName}</div>
                    {suggestion.displaySubtitle ? (
                      <div className="gear-suggestion-subtitle">
                        {suggestion.displaySubtitle}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
        </div>
      </div>

      {/* LEADER */}
      <div className="catch-form-field">
        <label>{renderGearLabel("leader", `${t("leader")}:`)}</label>
        <div style={{ position: "relative" }}>
          <input
            type="text"
            placeholder={t("enter_leader")}
            value={leader}
            onFocus={() => setActiveDropdown("Leader")}
            onChange={(e) => setLeader(e.target.value)}
          />
          {activeDropdown === "Leader" &&
            leader &&
            getSuggestions(leaderDatabase, leader).length > 0 && (
              <ul className="gear-suggestions">
                {getSuggestions(leaderDatabase, leader).map((s, i) => (
                  <li
                    key={i}
                    onClick={() => {
                      setLeader(s);
                      setActiveDropdown(null);
                    }}
                  >
                    {s}
                  </li>
                ))}
              </ul>
            )}
        </div>
      </div>

      {/* BAIT or LURE */}
      {isLureFishing(typeOfFishing) ? (
        <div className="catch-form-field">
          <label>{renderGearLabel("lure", `${t("lure")}:`)}</label>
          <div style={{ position: "relative" }}>
            <input
              type="text"
              placeholder={t("enter_lure")}
              value={lure}
              onFocus={() => setActiveDropdown("Lure")}
              onChange={(e) => setLure(e.target.value)}
            />
            {lureSuggestions.length > 0 && (
                <ul className="gear-suggestions">
                  {lureSuggestions.map((suggestion, index) => (
                    <li
                      key={`${suggestion.key}-${index}`}
                      onClick={() => {
                        setLure(suggestion.value);
                        setActiveDropdown(null);
                      }}
                    >
                      <div>{suggestion.displayName}</div>
                      {suggestion.displaySubtitle ? (
                        <div className="gear-suggestion-subtitle">
                          {suggestion.displaySubtitle}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </div>
      ) : (
        <div className="catch-form-field">
          <label>{renderGearLabel("lure", `${t("bait")}:`)}</label>
          <div style={{ position: "relative" }}>
            <input
              type="text"
              placeholder={t("enter_bait_used")}
              value={lure}
              onFocus={() => setActiveDropdown("Bait")}
              onChange={(e) => setLure(e.target.value)}
            />
            {activeDropdown === "Bait" &&
              lure &&
              getBaitSuggestions(lure).length > 0 && (
                <ul className="gear-suggestions">
                  {getBaitSuggestions(lure).map((s, i) => (
                    <li
                      key={i}
                      onClick={() => {
                        setLure(s);
                        setActiveDropdown(null);
                      }}
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </div>
      )}

      {/* HOOK BRAND or LURE SIZE */}
      {isLureFishing(typeOfFishing) ? (
        <div className="catch-form-field">
          <label>{renderGearLabel("lure", `${t("lure_size")}:`)}</label>
          <select value={lureSize} onChange={(e) => setLureSize(e.target.value)}>
            <option value="">{t("select_lure_size")}</option>
            {lureSizeOptions.map((opt, i) => (
              <option key={i} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="catch-form-field">
          <label>{renderGearLabel("hook", `${t("hook_brand")}:`)}</label>
          <div style={{ position: "relative" }}>
            <input
              type="text"
              placeholder={t("enter_hook_brand_model")}
              value={hookBrand}
              onFocus={() => setActiveDropdown("HookBrand")}
              onChange={(e) => setHookBrand(e.target.value)}
            />
            {activeDropdown === "HookBrand" &&
              hookBrand &&
              getHookSuggestions(hookBrandDatabase, hookBrand).length > 0 && (
                <ul className="gear-suggestions">
                  {getHookSuggestions(hookBrandDatabase, hookBrand).map((suggestion, i) => (
                    <li
                      key={`${suggestion.key}-${i}`}
                      onClick={() => {
                        setHookBrand(suggestion.value);
                        setActiveDropdown(null);
                      }}
                    >
                      <div>{suggestion.displayName}</div>
                      {suggestion.displaySubtitle ? (
                        <div className="gear-suggestion-subtitle">
                          {suggestion.displaySubtitle}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </div>
      )}

      {/* HOOK SIZE or LURE COLOR */}
      {isLureFishing(typeOfFishing) ? (
        <div className="catch-form-field">
          <label>{renderGearLabel("lure", `${t("lure_color")}:`)}</label>
          <div style={{ position: "relative" }}>
            <input
              type="text"
              placeholder={t("enter_lure_color")}
              value={lureColor}
              onFocus={() => setActiveDropdown("LureColor")}
              onChange={(e) => setLureColor(e.target.value)}
            />
            {activeDropdown === "LureColor" &&
              lureColor &&
              getColorSuggestions(lureColor).length > 0 && (
                <ul className="gear-suggestions">
                  {getColorSuggestions(lureColor).map((suggestion, i) => (
                    <li
                      key={`${suggestion.key}-${i}`}
                      onClick={() => {
                        setLureColor(suggestion.value);
                        setActiveDropdown(null);
                      }}
                    >
                      {suggestion.value}
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </div>
      ) : (
        <div className="catch-form-field">
          <label>{renderGearLabel("hook", `${t("hook_size")}:`)}</label>
          <select value={hookSpec} onChange={(e) => setHookSpec(e.target.value)}>
            <option value="">{t("select_hook_size")}</option>
            {combinedHookOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="gear-knot-grid">
        {/* Knot (Braid to Leader) */}
        <div
          className={`catch-form-field gear-knot-field${activeDropdown === "BraidKnot" ? " is-dropdown-open" : ""}`}
        >
          <label>
            {renderGearLabel(
              "braidToLeader",
              `${t("knot_braid_to_leader")}:`
            )}
          </label>
          <div style={{ position: "relative" }}>
            <input
              type="text"
              placeholder={t("knot_braid_to_leader_placeholder")}
              value={knotBraidToLeader}
              onFocus={() => setActiveDropdown("BraidKnot")}
              onChange={(e) => setKnotBraidToLeader(e.target.value)}
            />
            {activeDropdown === "BraidKnot" &&
              knotBraidToLeader &&
              getBraidKnotSuggestions(knotBraidToLeader).length > 0 && (
                <ul className="gear-suggestions">
                  {getBraidKnotSuggestions(knotBraidToLeader).map((s, i) => (
                    <li
                      key={i}
                      onClick={() => {
                        setKnotBraidToLeader(s);
                        setActiveDropdown(null);
                      }}
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </div>

        {/* Knot (Leader to Lure/Hook) */}
        <div
          className={`catch-form-field gear-knot-field${activeDropdown === "LeaderKnot" ? " is-dropdown-open" : ""}`}
        >
          <label>
            {renderGearLabel(
              "leaderToHookLure",
              `${t("knot_leader_to_lure")}:`
            )}
          </label>
          <div style={{ position: "relative" }}>
            <input
              type="text"
              placeholder={t("knot_leader_to_lure_placeholder")}
              value={knotLeaderToLure}
              onFocus={() => setActiveDropdown("LeaderKnot")}
              onChange={(e) => setKnotLeaderToLure(e.target.value)}
            />
            {activeDropdown === "LeaderKnot" &&
              knotLeaderToLure &&
              getLeaderKnotSuggestions(knotLeaderToLure).length > 0 && (
                <ul className="gear-suggestions">
                  {getLeaderKnotSuggestions(knotLeaderToLure).map((s, i) => (
                    <li
                      key={i}
                      onClick={() => {
                        setKnotLeaderToLure(s);
                        setActiveDropdown(null);
                      }}
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GearInput;
